import React, { useState } from 'react'
import VideoPlayer from './VideoPlayer'
import axios from 'axios';
import { API } from '../../services/api';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const VideoCard = ({ data, onUpdate }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API.GET_VIDEO_BY_ID}/${data._id}`, {
        withCredentials: true
      });
      toast.success('Video deleted successfully');
      onUpdate?.(); // Trigger parent component update
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete video');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Lecture': return '📚';
      case 'Interview': return '🎤';
      case 'Other': return '📹';
      default: return '📹';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if user is admin (you might want to get this from context/props)
  const isAdmin = true;

  return (
    <>
      <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl shadow-xl border border-gray-700/50 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{getCategoryIcon(data.category)}</span>
              <span className="px-3 py-1 bg-purple-600/20 text-purple-300 text-xs font-medium rounded-full border border-purple-500/30">
                {data.category}
              </span>
            </div>
            {data.createdAt && (
              <span className="text-xs text-gray-400">
                {formatDate(data.createdAt)}
              </span>
            )}
          </div>

          <h2 className="text-xl font-bold text-white mb-2 line-clamp-2 leading-tight">
            {data.title}
          </h2>
          
          {data.description && (
            <p className="text-gray-300 text-sm line-clamp-3 leading-relaxed mb-4">
              {data.description}
            </p>
          )}
        </div>

        {/* Video Player */}
        <div className="px-6 pb-4">
          <VideoPlayer title={data.title} link={data.youtubeLink} />
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <div className="px-6 pb-6">
            <div className="flex gap-3 pt-4 border-t border-gray-700/50">
              <Link 
                to={`/editvideo/${data._id}`}
                className="flex items-center gap-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 px-4 py-2 rounded-lg font-medium transition-all duration-200 border border-blue-500/30 hover:border-blue-400/50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </Link>
              
              <button 
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-300 px-4 py-2 rounded-lg font-medium transition-all duration-200 border border-red-500/30 hover:border-red-400/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-4xl mb-4">🗑️</div>
              <h3 className="text-xl font-bold text-white mb-2">Delete Video</h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete "<strong>{data.title}</strong>"? This action cannot be undone.
              </p>
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={cancelDelete}
                  className="px-6 py-2 bg-gray-600/50 hover:bg-gray-600/70 text-white rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete Video'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default VideoCard